<template>
    <div
    class="slider"
    :class="{ vertical }"
    @mousedown.prevent="onTrackPointerDown"
    @touchstart.prevent="onTrackPointerDown"
    ref="track"
    role="slider"
    :aria-valuemin="min"
    :aria-valuemax="max"
    :aria-valuenow="currentValue"
    :tabindex="0"
    @keydown="onKeydown"
    >
    <div class="slider__track">
        <div class="slider__track-filled" :style="filledStyle"></div>
        <div
        class="slider__thumb"
        :style="thumbStyle"
        @mousedown.stop.prevent="startDrag"
        @touchstart.stop.prevent="startDrag"
        ref="thumb"
        >
        <div v-if="showTooltip" class="slider__tooltip">{{ formattedValue }}</div>
    </div>
</div>
<!-- ticks (optional) -->
<div v-if="showTicks" class="slider__ticks">
    <span
    v-for="(t, idx) in ticks"
    :key="idx"
    class="slider__tick"
    :style="tickStyle(t)">
</span>
</div>
</div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'

export default {
    name: 'VSlider',
    props: {
        value: { type: Number, default: 0 },
        min: { type: Number, default: 0 },
        max: { type: Number, default: 100 },
        step: { type: Number, default: 1 },
        showTooltip: { type: Boolean, default: true },
        showTicks: { type: Boolean, default: false },
        tickCount: { type: Number, default: 5 },
        vertical: { type: Boolean, default: false },
        format: { type: Function, default: v => v }
    },
    emits: ['update:value', 'change', 'input'],
    data() {
        return {
            currentValue: this.value,
            dragging: false
        }
    },
    computed: {
        percent() {
            const range = this.max - this.min
            if (range === 0) return 0
            return ((this.currentValue - this.min) / range) * 100
        },
        filledStyle() {
            return this.vertical
            ? { height: `${this.percent}%` }
            : { width: `${this.percent}%` }
        },
        thumbStyle() {
            const offset = 10 // половина ширины thumb
            return this.vertical
            ? { bottom: `calc(${this.percent}% - ${offset}px)` }
            : { left: `calc(${this.percent}% - ${offset}px)` }
        },
        formattedValue() {
            return this.format(this.currentValue)
        },
        ticks() {
            if (!this.showTicks) return []
            const count = Math.max(2, this.tickCount)
            const arr = []
            for (let i = 0; i < count; i++) {
                const pct = (i / (count - 1)) * 100
                arr.push(pct)
            }
            return arr
        }
    },
    watch: {
        value(val) {
            this.currentValue = val
        },
        currentValue(val) {
            this.$emit('update:value', val)
            this.$emit('input', val)
        }
    },
    mounted() {
        nextTick(() => {
            this.currentValue = this.value
        })
        window.addEventListener('mousemove', this.onPointerMove)
        window.addEventListener('mouseup', this.stopDrag)
        window.addEventListener('touchmove', this.onPointerMove, { passive: false })
        window.addEventListener('touchend', this.stopDrag)
    },
    beforeUnmount() {
        window.removeEventListener('mousemove', this.onPointerMove)
        window.removeEventListener('mouseup', this.stopDrag)
        window.removeEventListener('touchmove', this.onPointerMove)
        window.removeEventListener('touchend', this.stopDrag)
    },
    methods: {
        clamp(v) {
            return Math.min(this.max, Math.max(this.min, v))
        },
        roundToStep(v) {
            const step = this.step || 1
            const inv = Math.round((v - this.min) / step) * step + this.min
            const decimals = Math.max(this.countDecimals(step), 0)
            return Number(inv.toFixed(decimals))
        },
        countDecimals(n) {
            const s = String(n)
            if (s.includes('.')) return s.split('.')[1].length
            return 0
        },
        clamp01(v) {
            return Math.min(1, Math.max(0, v))
        },
        positionToValue(clientX, clientY) {
            const rect = this.$refs.track.getBoundingClientRect()
            if (this.vertical) {
                const offsetY = rect.bottom - clientY
                const pct = this.clamp01(offsetY / rect.height)
                return this.roundToStep(this.min + pct * (this.max - this.min))
            } else {
                const offsetX = clientX - rect.left
                const pct = this.clamp01(offsetX / rect.width)
                return this.roundToStep(this.min + pct * (this.max - this.min))
            }
        },
        onPointerMove(e) {
            if (!this.dragging) return
            e.preventDefault()
            const touch = e.touches ? e.touches[0] : null
            const clientX = touch ? touch.clientX : e.clientX
            const clientY = touch ? touch.clientY : e.clientY
            const v = this.positionToValue(clientX, clientY)
            if (v !== this.currentValue) this.currentValue = v
        },
        startDrag(e) {
            this.dragging = true
        },
        stopDrag() {
            if (!this.dragging) return
            this.dragging = false
            this.$emit('change', this.currentValue)
        },
        onTrackPointerDown(e) {
            const touch = e.touches ? e.touches[0] : null
            const clientX = touch ? touch.clientX : e.clientX
            const clientY = touch ? touch.clientY : e.clientY
            const v = this.positionToValue(clientX, clientY)
            this.currentValue = v
            this.$emit('change', this.currentValue)
            this.dragging = true
        },
        onKeydown(e) {
            let v = this.currentValue
            if (e.key === 'ArrowRight' || e.key === 'ArrowUp') v += this.step
            if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') v -= this.step
            if (e.key === 'Home') v = this.min
            if (e.key === 'End') v = this.max
            v = this.clamp(this.roundToStep(v))
            if (v !== this.currentValue) {
                this.currentValue = v
                this.$emit('change', v)
            }
        },
        tickStyle(pct) {
            return this.vertical
            ? { bottom: `calc(${pct}% - 1px)` }
            : { left: `calc(${pct}% - 1px)` }
        }
    }
}
</script>


<style lang="scss" scoped>
$slider-sizes: (
small: 6px,
middle: 8px,
large: 12px
);

.slider {
    width: 100%;
    min-width: 100px;
    max-width: 400px;
    padding: 12px 0;
    user-select: none;
    
    &.vertical {
        height: 200px;
        width: 36px;
        padding: 0 12px;
    }
    
    // Sizes
    @each $name, $trackHeight in $slider-sizes {
        &.--#{$name} {
            .slider__track {
                height: $trackHeight;
            }
            .slider.vertical .slider__track {
                width: $trackHeight;
            }
            .slider__thumb {
                width: $trackHeight * 2.5;
                height: $trackHeight * 2.5;
            }
            .slider__tooltip {
                font-size: if($name == small, 10px, if($name == large, 14px, 12px));
                padding: if($name == small, 2px 6px, if($name == large, 6px 10px, 4px 8px));
            }
        }
    }
    
    &.--minimal {
        .slider__ticks {
            display: none;
        }
        .slider__thumb {
            display: none;
        }
        .slider__tooltip {
            display: none;
        }
    }
    
    &__track {
        position: relative;
        height: 8px;
        background: #e6e6e6;
        border-radius: 999px;
        
        .vertical & {
            width: 8px;
            height: 100%;
            margin: 0 auto;
        }
    }
    
    &__track-filled {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        background: linear-gradient(90deg, #f9c30c, #f7e279);
        border-radius: 999px;
        
        .vertical & {
            width: 100%;
            bottom: 0;
            top: auto;
        }
    }
    
    &__thumb {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #3a4146;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
        border: 2px solid rgba(0, 0, 0, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        
        .vertical & {
            left: 50%;
            transform: translateX(-50%);
        }
    }
    
    &__tooltip {
        position: absolute;
        //top: -34px;
        bottom: 100%;
        margin-bottom: 4px;
        padding: 4px 8px;
        font-size: 12px;
        border-radius: 4px;
        background: #3a4146;
        color: white;
        white-space: nowrap;
        
        .vertical & {
            left: 28px;
            top: 50%;
            transform: translateY(-50%);
        }
    }
    
    &__ticks {
        margin-top: 8px;
        position: relative;
        
        .vertical & {
            margin-top: 0;
            margin-left: 18px;
            height: 100%;
        }
    }
    
    &__tick {
        position: absolute;
        width: 2px;
        height: 8px;
        background: rgba(0, 0, 0, 0.2);
    }
}
</style>